import React, { useCallback, useMemo } from 'react'

import { Box, FlexBox } from '@surglogs-app/ui'

import { MultiFileInputProps } from '../types'

import { DragAndDropArea } from './DragAndDropArea'
import { UploadedFileItem } from './UploadedFileItem'

interface Props extends MultiFileInputProps {
  id: string
}

export const MultiFileInputContent = ({
  fileName,
  uploadProgress,
  thumbnail,
  url,
  onRemoveClick,
  isInvalid,
  ...props
}: Props) => {
  const createOnRemoveHandler = useCallback(
    (index: number) => () => onRemoveClick?.(index),
    [onRemoveClick],
  )

  // Because of backwards compatibility we don't encapsulate file props into separate
  // objects and rather re-use separate props with adjusting respective types to be array
  const fileItems: React.ComponentProps<typeof UploadedFileItem>[] = useMemo(
    () =>
      fileName?.map((name, index) => ({
        fileName: name ?? '',
        uploadProgress: uploadProgress?.[index],
        thumbnail: thumbnail?.[index],
        url: url?.[index],
        onRemoveClick: createOnRemoveHandler(index),
        isInvalid: isInvalid?.[index],
      })) ?? [],
    [
      fileName,
      uploadProgress,
      thumbnail,
      url,
      createOnRemoveHandler,
      isInvalid,
    ],
  )

  return (
    <Box>
      <DragAndDropArea {...props} />
      {fileItems?.length > 0 ? (
        <FlexBox
          {...{ flex: '1', ai: 'center', fd: 'column', gap: 'md', mt: 'md' }}>
          {fileItems.map((itemProps, index) => (
            <UploadedFileItem key={`file-item-${index}`} {...itemProps} />
          ))}
        </FlexBox>
      ) : null}
    </Box>
  )
}
